// Helper to persist layer order to localStorage
function persistLayerOrder(orgId, layers) {
  const layerOrder = layers.map(l => l.uuid)
  localStorage.setItem(`layerOrder_${orgId}`, JSON.stringify(layerOrder))
}

// Helper to get saved layer order from localStorage
function getSavedLayerOrder(orgId) {
  const saved = localStorage.getItem(`layerOrder_${orgId}`)
  if (!saved) return null
  try {
    const parsed = JSON.parse(saved)
    return Array.isArray(parsed) ? parsed : null
  } catch (e) {
    console.warn('Failed to parse layer order from localStorage:', e)
    return null
  }
}

export const mutations = {
  SET_LAYERS(state, payload) {
    state.layers = state.layers.concat(payload)
  },
  UPDATE_LAYER(state, payload) {
    state.layers = state.layers.map(layer => {
      if (layer.uuid === payload.uuid) {
        return payload
      }
      return layer
    })
  },
  // Organization layers mutations
  SET_ORG_LAYERS(state, { orgId, layers }) {
    const existingLayers = state.orgLayers[orgId] || []
    const existingLayersMap = new Map(existingLayers.map(layer => [layer.uuid, layer]))

    const mergedLayers = layers.map(newLayer => {
      const existingLayer = existingLayersMap.get(newLayer.uuid)

      if (existingLayer && existingLayer.properties) {
        // Preserve existing client-side state when merging
        const preserved = {}
        if (existingLayer.properties.features) {
          preserved.features = existingLayer.properties.features
          preserved.featureCollection = existingLayer.properties.featureCollection || {
            type: 'FeatureCollection',
            features: existingLayer.properties.features
          }
        }
        if (existingLayer.properties.hiddenCaptures) {
          preserved.hiddenCaptures = existingLayer.properties.hiddenCaptures
        }
        if (existingLayer.properties.visible !== undefined) {
          preserved.visible = existingLayer.properties.visible
        }

        if (Object.keys(preserved).length > 0) {
          return {
            ...newLayer,
            properties: {
              ...newLayer.properties,
              ...preserved
            }
          }
        }
      }

      return newLayer
    })

    // Restore saved layer order from localStorage
    const orderArray = getSavedLayerOrder(orgId)
    let orderedLayers = mergedLayers

    if (orderArray) {
      const layerMap = new Map(mergedLayers.map(l => [l.uuid, l]))

      // Sort by saved order, putting unknown layers at the end
      orderedLayers = orderArray
        .filter(uuid => layerMap.has(uuid))
        .map(uuid => layerMap.get(uuid))

      // Add any new layers not in saved order at the beginning
      const savedSet = new Set(orderArray)
      const newLayers = mergedLayers.filter(l => !savedSet.has(l.uuid))
      orderedLayers = [...newLayers, ...orderedLayers]
    }

    state.orgLayers = {
      ...state.orgLayers,
      [orgId]: orderedLayers
    }
  },
  ADD_ORG_LAYER(state, { orgId, layer }) {
    if (!state.orgLayers[orgId]) {
      state.orgLayers[orgId] = []
    }
    state.orgLayers[orgId] = [layer, ...state.orgLayers[orgId]]
  },
  UPDATE_ORG_LAYER(state, { orgId, layer }) {
    console.log('UPDATE_ORG_LAYER', state.orgLayers, layer)
    if (state.orgLayers[orgId]) {
      state.orgLayers[orgId] = state.orgLayers[orgId].map(l => {
        if (l.uuid === layer.uuid) {
          return layer
        }
        return l
      })
    }
  },
  UPDATE_ORG_LAYER_FEATURES(state, { orgId, layerId, features }) {
    if (state.orgLayers[orgId]) {
      // Extract thumbUrl from resources for each feature (for image markers)
      const processedFeatures = features.map(f => {
        const resources = f.properties?.resources
        const thumbUrl = resources?.find(r => r.url?.includes('small'))?.url
                      || resources?.find(r => r.url?.includes('thumbnail'))?.url
        if (thumbUrl) {
          return {
            ...f,
            properties: {
              ...f.properties,
              thumbUrl
            }
          }
        }
        return f
      })

      state.orgLayers[orgId] = state.orgLayers[orgId].map(l => {
        if (l.uuid === layerId) {
          return {
            ...l,
            properties: {
              ...l.properties,
              features: processedFeatures,
              featureCollection: {
                type: 'FeatureCollection',
                features: processedFeatures
              }
            }
          }
        }
        return l
      })
    }
  },
  RENAME_ORG_LAYER(state, { orgId, layerId, name }) {
    if (state.orgLayers[orgId]) {
      state.orgLayers[orgId] = state.orgLayers[orgId].map(l => {
        if (l.uuid === layerId) {
          return {
            ...l,
            properties: {
              ...l.properties,
              name
            }
          }
        }
        return l
      })
    }
  },
  DELETE_ORG_LAYER(state, { orgId, layerId }) {
    if (state.orgLayers[orgId]) {
      state.orgLayers[orgId] = state.orgLayers[orgId].filter(layer => layer.uuid !== layerId)
    }
  },
  MOVE_ORG_LAYER_UP(state, { orgId, layerId }) {
    if (state.orgLayers[orgId]) {
      const layers = [...state.orgLayers[orgId]]
      const index = layers.findIndex(layer => layer.uuid === layerId)
      if (index > 0) {
        // Swap with the layer above (index - 1)
        const temp = layers[index - 1]
        layers[index - 1] = layers[index]
        layers[index] = temp
        state.orgLayers = { ...state.orgLayers, [orgId]: layers }
        persistLayerOrder(orgId, layers)
      }
    }
  },
  MOVE_ORG_LAYER_DOWN(state, { orgId, layerId }) {
    if (state.orgLayers[orgId]) {
      const layers = [...state.orgLayers[orgId]]
      const index = layers.findIndex(layer => layer.uuid === layerId)
      if (index >= 0 && index < layers.length - 1) {
        // Swap with the layer below (index + 1)
        const temp = layers[index + 1]
        layers[index + 1] = layers[index]
        layers[index] = temp
        state.orgLayers = { ...state.orgLayers, [orgId]: layers }
        persistLayerOrder(orgId, layers)
      }
    }
  },
  REORDER_ORG_LAYERS(state, { orgId, layers }) {
    // Direct reorder - just set the new order
    state.orgLayers = { ...state.orgLayers, [orgId]: layers }
    persistLayerOrder(orgId, layers)
  },
  ADD_DATES(state, payload) {
    try {
      const existingDatesMap = new Map();

      if (state.dates && Array.isArray(state.dates)) {
        state.dates.forEach(date => {
          if (date && date.id) {
            existingDatesMap.set(date.id, date);
          }
        });
      }

      const payloadArray = Array.isArray(payload) ? payload : [payload];
      payloadArray.forEach(newDate => {
        if (!newDate || typeof newDate !== 'object') return;

        if (newDate.id === 'features' && newDate.layers && Array.isArray(newDate.layers)) {
          newDate.layers.forEach(templateLayer => {
            if (templateLayer && templateLayer.id) {
              const safeTemplateLayer = {
                id: templateLayer.id,
                name: templateLayer.name || 'Unknown Template',
                color: templateLayer.color || 'blue',
                dates: Array.isArray(templateLayer.dates) ? [...templateLayer.dates] : []
              };
              existingDatesMap.set(templateLayer.id, safeTemplateLayer);
            }
          });
        }
        else if (newDate.id) {
          const safeDateObject = {
            id: newDate.id,
            name: newDate.name || newDate.id || 'Unknown',
            color: newDate.color || 'blue',
            dates: Array.isArray(newDate.dates) ? [...newDate.dates] : []
          };
          existingDatesMap.set(newDate.id, safeDateObject);
        }
      });

      state.dates = Array.from(existingDatesMap.values());
    } catch (err) {
      console.error('Error in ADD_DATES mutation:', err);
    }
  },
  CLEAR_DATES(state) {
    state.dates = [];
  },
  REMOVE_DATES(state, dateIds) {
    if (!Array.isArray(dateIds) || !state.dates) return
    const idsToRemove = new Set(dateIds)
    state.dates = state.dates.filter(date => !idsToRemove.has(date.id))
  },
  SET_SELECTED_RANGE(state, payload) {
    state.selectedRange = payload
  },
  SET_SELECTED_FEATURE(state, { feature, coordinates, isVisible }) {
    state.selectedFeature = {
      feature,
      coordinates,
      isVisible
    }
  },
  UPDATE_LAYER_PROPERTIES(state, { orgId, layerId, properties }) {
    if (state.orgLayers[orgId]) {
      state.orgLayers[orgId] = state.orgLayers[orgId].map(l => {
        if (l.uuid === layerId) {
          return {
            ...l,
            properties: {
              ...l.properties,
              ...properties
            }
          }
        }
        return l
      })
    }
  },
  SET_ACTIVE_RESULT(state, { layerId, result }) {
    state.activeResults = {
      ...state.activeResults,
      [layerId]: result
    }
  },
  SET_SUBSCRIPTION_RESULTS(state, { orgId, results }) {
    state.subscriptionResults = {
      ...state.subscriptionResults,
      [orgId]: results
    }
  },
  SET_PLANETSCOPE_SELECTION(state, { layerId, selection }) {
    state.planetscopeSelection = {
      ...state.planetscopeSelection,
      [layerId]: selection
    }
  },
  SET_ORDER_PRODUCT_BUNDLE(state, { layerId, bundle }) {
    state.orderProductBundle = {
      ...state.orderProductBundle,
      [layerId]: bundle
    }
  }
}
